<template>
  <div id="graph-container">
    <svg :viewBox="`0 0 ${graphWidth} ${graphHeight}`">
      <text 
        v-if="showTitle"
        :transform="`translate( ${graphWidth/2}, ${margin.top} )`"
      >
        {{ component.label }}
      </text>

      <g v-if="Array.isArray(labels)" class="legend" :transform="`translate(${margin.left}, ${margin.top})`">
        <g v-for="(label,index) in labels" :key="index">
          <text :transform="`translate(10, ${10+16*index})`" :class="`line-number-${index+1}`">
            {{ label }}
          </text>
        </g>
      </g>

      <path v-for="(datum,index) in datasArray" :key="index"
        :d="line(datum)"
        :class="`line line-number-${index+1}`">
      </path>

      <g ref="xaxis"></g>
      <g ref="yaxis"></g>
    </svg>
  </div>
</template>

<script>
import { TimeSeries, DateDatum } from '@/js/model/DataSeries';
import { makeArray } from '@/js/model/Util';
import { formatGenericNumber, formatGenericDate } from '@/js/d3/Formats';

const d3 = require('d3');


export default {
  name: 'TimeSeriesGraph',
  props: {
    datas: { type: Array },
    labels: { default: '' },
    showTitle: {type: Boolean, default: true},
    showLegend: {type: Boolean, default: true},
    graphHeight: {type: Number, default: 250},
    graphWidth: {type: Number, default: 600},
    margin: {
      type: Object,
      default: () => ({'top': 20, 'right': 20, 'bottom': 30, 'left': 50})
    }
  },
  mounted() {
    d3.select('#graph-container')
        .style('max-width', `${this.graphWidth}px`)
        .style('max-height', `${this.graphHeight}px`);
    this.refreshAxes();
  },
  watch: {
    datas: function() {
      this.refreshAxes();
    }
  },
  methods: {
    refreshAxes() {
      let xAxisFunc = d3.axisBottom(this.xScale).ticks(this.graphWidth / 80);
      if (this.isDateSeries) xAxisFunc.tickFormat(formatGenericDate);

      let xAxis = g => g
        .attr('transform', `translate(0, ${this.xAxisLocation})`)
        .call(xAxisFunc);

      let yAxis = g => g
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .call(d3.axisLeft(this.yScale).tickFormat(formatGenericNumber));
      
      d3.select(this.$refs.yaxis).attr("class", "axis").call(yAxis);
      d3.select(this.$refs.xaxis).attr("class", "axis").call(xAxis);
    },
    referencePoint(model) {
      let optionTypes = model.forecast.getCalculationOptionTypes();
      if (optionTypes !== null) {
        let index = optionTypes.findIndex(i => i.name === 'Reference Point');
        if (index >= 0) {
          let parameters = model.forecast.getCalculationParameter();
          let dataPoint = model.forecast.cache.data[parameters[index]];
          return {
            "cx": this.xScale(dataPoint.index),
            "cy": this.yScale(dataPoint.value),
            "r": 2.5
          };
        }
      }
      return null;
    }
  },
  computed: {
    datasArray() {
      return makeArray(this.datas);
    },
    isDateSeries() {
      return this.datasArray[0][0] instanceof DateDatum;
    },
    xScale() {
      let values = this.datasArray.map(d => d.getIndices()).flat(1);
      let scale = this.isDateSeries ? d3.scaleUtc() : d3.scaleLinear();

      return scale.domain(d3.extent(values))
        .range([this.margin.left, this.graphWidth - this.margin.right]);
    },
    xAxisLocation() {
      let yDomain = this.yScale.domain();
      if (yDomain[0] !== 0 && yDomain[1] === 0) {
        return this.margin.top;
      } else if (yDomain[0] === 0 && yDomain[1] !== 0) {
        return this.graphHeight - this.margin.bottom;
      } else {
        return this.yScale(0) || 0; 
      }
    },
    yScale() {
      let axisBuffer = 1.1;
      let values = this.datasArray.map(d => d.getValues()).flat(1);
      
      let yDomains = d3.zip(
        d3.extent(values),
      );

      let yDomain = [
        d3.min(yDomains[0]) * axisBuffer,
        d3.max(yDomains[1]) * axisBuffer
      ];

      if (Math.sign(yDomain[0]) === Math.sign(yDomain[1])) {
        if (yDomain[0] < 0) {
          yDomain[1] = 0;
        } else {
          yDomain[0] = 0;
        }
      }

      return d3.scaleLinear()
        .domain(yDomain)
        .range([this.graphHeight - this.margin.bottom, this.margin.top]);
    },
    line() {
      if (!this.datas) return () => {};
      return d3.line()
        .defined(d => d.value !== null)
        .x(d => this.xScale(d.index))
        .y(d => this.yScale(d.value));
    },
    forecastReferencePoint() {
      return this.referencePoint(this.model);
    }      
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#graph-container {
  margin: auto;
}

.forecast-line {
  stroke: #75b0d2;
  stroke-dasharray: 4 2;
  stroke-width: 1.5;
  fill: none;
}

.forecast-line-two {
  stroke: #00bc83;
  stroke-dasharray: 4 2;
  stroke-width: 1.5;
}

.line {
  stroke: black;
  stroke-width: 1;
  fill: none;
}

text.line-number-2 { fill: #00bc83; }
path.line-number-2 { stroke: #00bc83; }

text.line-number-3 { fill: #0089cd; }
path.line-number-3 { stroke: #0089cd; }

text.line-number-4 { fill: #be374c; }
path.line-number-4 { stroke: #be374c; }


.reference-point {
  fill: #75b0d2;
}

.reference-point-two {
  fill: #00bc83;
}

.axis {
  font-size: .9em;
  font-weight: bold;
}

.legend {
  font-size: 13px;
}

h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
</style>
